import React, { useCallback, useEffect, useState } from "react";
import {
  Table,
  Radio,
  Button,
  Modal,
  Input,
  Space,
  message,
  Tag,
  Typography,
} from "antd";
import { ExclamationCircleOutlined } from "@ant-design/icons";
import moment from "moment";
import { getViolations, reviewAppeal } from "../../Services/moderationApi";
import StatusPill from "../../Components/Moderation/StatusPill";

const { Paragraph } = Typography;

const DURATION_LABEL = {
  "3_days": "3 days",
  "7_days": "7 days",
  permanent: "Permanent",
};

const Appeals = () => {
  const [tab, setTab] = useState("pending");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      // Appeals live on violations. A submitted appeal flips the violation
      // status to "appealed"; a rejected one flips back to "active" and a
      // approved one to "revoked" — so for the reviewed tab we pull all.
      const resp =
        tab === "pending"
          ? await getViolations({ status: "appealed" })
          : await getViolations({});
      const all = resp?.data?.data || [];
      const filtered =
        tab === "pending"
          ? all.filter((v) => v.appeal_status === "pending")
          : all.filter(
              (v) =>
                v.appeal_status === "approved" || v.appeal_status === "rejected"
            );
      setRows(filtered);
    } catch (err) {
      message.error("Could not load appeals");
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [tab]);

  useEffect(() => {
    load();
  }, [load]);

  const decide = (violation, approved) => {
    let note = "";
    Modal.confirm({
      title: approved ? "Approve this appeal?" : "Reject this appeal?",
      icon: <ExclamationCircleOutlined />,
      content: (
        <div>
          <p>
            {approved
              ? "The suspension is lifted immediately and the user is notified."
              : "The suspension stands. The user is notified with your note."}
          </p>
          <Input.TextArea
            rows={3}
            placeholder={
              approved
                ? "Note to the user (optional)"
                : "Reason shown to the user (recommended)"
            }
            onChange={(e) => {
              note = e.target.value;
            }}
          />
        </div>
      ),
      okText: approved ? "Approve" : "Reject",
      okButtonProps: { danger: !approved },
      cancelText: "Cancel",
      onOk: async () => {
        try {
          const resp = await reviewAppeal(
            violation.id || violation._key,
            approved,
            note.trim()
          );
          if (resp?.data?.error) {
            message.error(resp.data.error);
            throw new Error(resp.data.error);
          }
          message.success(resp?.data?.message || "Decision recorded");
          load();
        } catch (err) {
          if (err?.response?.data?.error) message.error(err.response.data.error);
          throw err;
        }
      },
    });
  };

  const columns = [
    {
      title: "User",
      dataIndex: "user",
      render: (u) => (u && u.name ? u.name : "(no name)"),
    },
    {
      title: "Strike",
      dataIndex: "strike_number",
      render: (n) => <Tag>{n}</Tag>,
    },
    {
      title: "Suspension",
      dataIndex: "suspension_type",
      render: (t) => DURATION_LABEL[t] || t,
    },
    {
      title: "Appeal text",
      dataIndex: "appeal_text",
      render: (t) => (
        <Paragraph style={{ margin: 0, maxWidth: 320 }} ellipsis={{ rows: 2, expandable: true }}>
          {t || "—"}
        </Paragraph>
      ),
    },
    {
      title: "Submitted",
      dataIndex: "appealed_at",
      render: (d) => (d ? moment(d).format("DD MMM HH:mm") : "—"),
      responsive: ["lg"],
    },
    {
      title: "Appeal",
      dataIndex: "appeal_status",
      render: (s) => <StatusPill status={s} />,
    },
    {
      title: "",
      key: "action",
      render: (_, row) =>
        row.appeal_status === "pending" ? (
          <Space>
            <Button size="small" type="primary" onClick={() => decide(row, true)}>
              Approve
            </Button>
            <Button size="small" danger onClick={() => decide(row, false)}>
              Reject
            </Button>
          </Space>
        ) : row.appeal_admin_note ? (
          <span style={{ color: "#999" }}>{row.appeal_admin_note}</span>
        ) : null,
    },
  ];

  return (
    <div style={{ padding: "0 4px" }}>
      <Radio.Group
        value={tab}
        onChange={(e) => setTab(e.target.value)}
        style={{ marginBottom: 16 }}
      >
        <Radio.Button value="pending">Pending</Radio.Button>
        <Radio.Button value="reviewed">Reviewed</Radio.Button>
      </Radio.Group>

      <Table
        rowKey={(r) => r.id || r._key}
        columns={columns}
        dataSource={rows}
        loading={loading}
        pagination={{ pageSize: 20 }}
      />
    </div>
  );
};

export default Appeals;
